import Axios from 'axios';
import { Subject } from '@/models/subject';
import { SchedulePerson } from '@/models/schedulePerson';

interface ScheduleCalendarHeaderDate {
  date: Date | null;
  weekday: number;
}

enum ScheduleCalendarMode {
  BaseSchedule = 0,
  TempCalendar,
  Calendar,
  Reservation,
}

enum ScheduleMoveMode {
  None = 0,
  Move = 1,
  Copy = 2,
  CopyAndBeforeEdit = 3,
}

/**
 * 基本スケジュール
 *
 * @interface Schedule
 */
interface Schedule {
  scheduleId: number; // スケジュールのユニークＩＤ
  weekday: number; // 曜日 0 - 6
  unixtimeDate: number | null; // 日にち
  stringStart: string; // 開始時間 hh:ii
  stringEnd: string; // 終了時間 hh:ii
  subjects: Subject[]; // 教科一覧
  capacity: number; // 定員
  reservers: SchedulePerson[]; // 応募者
  memo: string; // お知らせ
}

/**
 * 講師単位の基本スケジュール
 *
 * @interface TutorSchedule
 */
interface TutorSchedule {
  tutorId: string; // 講師ＩＤ
  tutorName: string; // 講師名
  enableSubjects: Subject[]; // 選択できる教科
  schedules: Schedule[]; // スケジュール一覧
}

interface TutorSelector {
  tutorId: string;
  tutorName: string;
  enableSubjects: Subject[];
}

interface ScheduleRequest {
  calendarId: number;
  scheduleId: number;
  tutorId: string;
  weekday: number;
  date: Date | null;
  stringStart: string;
  stringEnd: string;
  subjectIds: number[];
  capacity: number;
  memo: string;
  copyReservers: boolean;
}

// 型の Export
export {
  ScheduleCalendarHeaderDate,
  ScheduleCalendarMode,
  ScheduleMoveMode,
  Schedule,
  TutorSchedule,
  TutorSelector,
  ScheduleRequest
};

interface State {
  isLoading: boolean;
  baseSchedules: TutorSchedule[];
}

const mapState = {
  isLoading: false,
  baseSchedules: [],
};

const mapMutations = {
  /**
   * ローディングフラグの登録
   * @param state
   * @param isLoading
   */
  setIsLoading(state: State, isLoading: boolean) {
    state.isLoading = isLoading;
  },
  /**
   * スケジュールの登録
   * @param state
   * @param schedules
   */
  setBaseSchedules(state: State, schedules: TutorSchedule[]) {
    state.baseSchedules = [];
    Object.assign(state.baseSchedules, schedules);

    state.isLoading = false;
  }
};

const mapActions = {
  /**
   * スケジュールの取得
   */
  async getBaseSchedules({ commit }: any) {
    commit('setIsLoading', true);
    const response = await Axios.post<TutorSchedule[]>('/api/Schedule/BaseSchedules').catch((error) => {
      commit('setBaseSchedules', []);
      throw error;
    });
    commit('setBaseSchedules', response.data);
  },

  /**
   * 新規作成
   * @param {*} { commit }
   * @param {ScheduleRequest} request
   */
  async createBaseScheduleRequest({ commit, dispatch }: any, request: ScheduleRequest) {
    const response = await Axios.post<boolean>('/api/Schedule/CreateBaseSchedule', request).catch((error) => {
      commit('setBaseSchedules', []);
    });
    if (!response) { return; }
    return response.data;
  },

  /**
   * 基本スケジュールの編集
   * @param param0
   * @param request
   */
  async editBaseScheduleRequest({ commit, dispatch }: any, request: ScheduleRequest) {
    const response = await Axios.post<boolean>('/api/Schedule/EditBaseSchedule', request).catch((error) => {
      commit('setBaseSchedules', []);
    });
    if (!response) { return; }
    return response.data;
  },

  async removeBaseScheduleRequest({ commit, dispatch }: any, scheduleId: number) {
    const response = await Axios.post<boolean>('/api/Schedule/DeleteBaseSchedule', {
      scheduleId,
    }).catch((error) => {
      commit('setBaseSchedules', []);
    });
    if (!response) { return false; }
    return true;
  },

  async copyBaseScheduleRequest({ commit, dispatch }: any, request: ScheduleRequest) {
    const response = await Axios.post<boolean>('/api/Schedule/CopyBaseSchedule', request).catch((error) => {
      commit('setBaseSchedules', []);
    });
    if (!response) { return; }
    return response.data;
  },

  async addBaseScheduleReserver(
    {commit}: any,
    request: {baseScheduleId: number, studentId: string, subjectId: string}
  ) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/schedule/AddBaseScheduleReserver', request);
    commit('setIsLoading', false);
    if (response) {
      return response.data;
    }
    return {message: '生徒の予約に失敗しました'};
  },

  async removeBaseScheduleReserver({commit}: any, request: {baseScheduleId: number, studentId: string}) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/schedule/DeleteBaseSchedlueReserver', request);
    commit('setIsLoading', false);
    if (response) {
      return response.data;
    }
    return {message: '生徒の予約に失敗しました'};
  }
};

// ストアの Export
export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
